import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, style, transition, trigger } from '@angular/animations';
import { fromEvent } from 'rxjs';

@Component({
  selector: 'ss-popover',
  standalone: true,
  imports: [],
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('popoverAnimation', [
      // Bottom position: slides down from above
      transition('void => bottom', [
        style({ opacity: 0, transform: 'translateX(-50%) translateY(-8px)' }),
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateX(-50%) translateY(0)' })
        ),
      ]),
      // Top position: slides up from below
      transition('void => top', [
        style({ opacity: 0, transform: 'translateX(-50%) translateY(8px)' }),
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateX(-50%) translateY(0)' })
        ),
      ]),
      // Left position: slides from the right
      transition('void => left', [
        style({ opacity: 0, transform: 'translateY(-50%) translateX(8px)' }),
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(-50%) translateX(0)' })
        ),
      ]),
      // Right position: slides from the left
      transition('void => right', [
        style({ opacity: 0, transform: 'translateY(-50%) translateX(-8px)' }),
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(-50%) translateX(0)' })
        ),
      ]),
      // All positions fade out the same way
      transition('* => void', [
        animate('150ms cubic-bezier(0.4, 0.0, 1, 1)', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class PopoverComponent {
  dismissable = input<boolean>(true);
  styleClass = input<string>('');

  position = input<'top' | 'bottom' | 'left' | 'right'>('bottom');
  appendToBody = input<boolean>(false);
  visibilityChange = output<boolean>();
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  isVisible = signal(false);
  popoverContent = viewChild<ElementRef>('popoverContent');
  triggerElement = viewChild<ElementRef>('triggerWrapper');

  popoverPosition = signal<{ top: string; left: string }>({
    top: '0',
    left: '0',
  });

  private animationFrameId: number | null = null;

  popoverClasses = computed(() => {
    const classes = [
      this.appendToBody() ? 'fixed' : 'absolute',
      'z-[1000]',
      'bg-white',
      'border',
      'border-gray-200',
      'rounded-lg',
      'shadow-lg',
      this.appendToBody() ? 'min-w-[80px]' : 'min-w-[150px]',
      this.appendToBody() ? 'max-w-[180px]' : 'max-w-[200px]',
      `popover-${this.position()}${this.appendToBody() ? '-body' : ''}`,
    ];

    if (this.styleClass()) {
      classes.push(this.styleClass());
    }

    return classes.join(' ');
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      fromEvent<MouseEvent>(this.document, 'click')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => this.onDocumentClick(event));

      fromEvent<KeyboardEvent>(this.document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => this.onKeyDown(event));
    }

    this.destroyRef.onDestroy(() => {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
    });
  }

  toggle() {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    this.isVisible.set(true);

    if (this.appendToBody() && isPlatformBrowser(this.platformId)) {
      this.animationFrameId = requestAnimationFrame(() => {
        this.updatePosition();
        this.animationFrameId = null;
      });
    }

    this.visibilityChange.emit(true);
  }

  private updatePosition() {
    const trigger = this.triggerElement()?.nativeElement;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const position = this.position();

    let top = 0;
    let left = 0;

    switch (position) {
      case 'bottom':
        top = rect.bottom;
        left = rect.left + rect.width / 2;
        break;
      case 'top':
        top = rect.top;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right;
        break;
    }

    this.popoverPosition.set({
      top: `${top}px`,
      left: `${left}px`,
    });
  }

  hide() {
    if (this.isVisible()) {
      this.isVisible.set(false);
      this.visibilityChange.emit(false);
    }
  }

  private onDocumentClick(event: MouseEvent) {
    if (!this.dismissable() || !this.isVisible()) {
      return;
    }

    const popoverElement = this.popoverContent()?.nativeElement;
    const triggerElement = this.triggerElement()?.nativeElement;
    const clickedElement = event.target as Node;

    const clickedInsidePopover =
      popoverElement?.contains(clickedElement) ?? false;
    const clickedInsideTrigger =
      triggerElement?.contains(clickedElement) ?? false;

    if (!clickedInsidePopover && !clickedInsideTrigger) {
      this.hide();
    }
  }

  private onKeyDown(event: KeyboardEvent) {
    const triggerElement = this.triggerElement()?.nativeElement;

    if (event.key === 'Escape' && this.isVisible()) {
      event.preventDefault();
      this.hide();

      const triggerButton = triggerElement?.querySelector(
        'button'
      ) as HTMLElement;
      if (triggerButton) {
        triggerButton.focus();
      }
    }
  }
}
